import { FormEvent, ReactNode, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import OrgSearchSelect from '@/client/components/common/OrgSearchSelect';
import UserSearchSelect from '@/client/components/common/UserSearchSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { PaymentUrlState } from '@/client/hooks/usePaymentListUrlState';

type Option = { value: string; label: string };

/** Digits apply as the id immediately. A name stays local until a search row is picked. */
function useIdOrName(id: string, onId: (next: string) => void) {
  const [text, setText] = useState(id);
  const applied = useRef(id);
  const seen = useRef(id);
  if (id !== seen.current) {
    seen.current = id;
    if (id !== applied.current) {
      applied.current = id;
      setText(id);
    }
  }
  return {
    text,
    onChange: (value: string) => {
      setText(value);
      const trimmed = value.trim();
      if (!trimmed || /^\d+$/.test(trimmed)) {
        applied.current = trimmed;
        if (trimmed !== id) onId(trimmed);
      } else if (id) {
        applied.current = '';
        onId('');
      }
    },
    onPick: (nextId: string, label: string) => {
      applied.current = nextId;
      setText(label || nextId);
      if (nextId !== id) onId(nextId);
    },
  };
}

export function PaymentsFilterBar({
  state,
  onPatch,
  onClear,
  onSubmit,
  statusOptions,
  billingTypeOptions,
  billingStatusOptions,
  fulfilledOptions,
  showAmount = false,
  showDates = true,
  showOid = true,
  showCcode = true,
  searchPlaceholder = 'Search ffid, uuid, or id…',
  extra,
}: {
  state: PaymentUrlState;
  onPatch: (u: Partial<Record<keyof PaymentUrlState, string | number | null | undefined>>) => void;
  onClear: () => void;
  onSubmit?: (e: FormEvent) => void;
  statusOptions?: Option[];
  billingTypeOptions?: Option[];
  billingStatusOptions?: Option[];
  fulfilledOptions?: Option[];
  showAmount?: boolean;
  showDates?: boolean;
  showOid?: boolean;
  showCcode?: boolean;
  searchPlaceholder?: string;
  extra?: ReactNode;
}) {
  const user = useIdOrName(state.uid, (uid) => onPatch({ uid }));
  const org = useIdOrName(state.oid, (oid) => onPatch({ oid }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit?.(e);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={state.q}
            onChange={(e) => onPatch({ q: e.target.value })}
            placeholder={searchPlaceholder}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" size="sm">
            Apply
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={onClear}>
            <X className="mr-1 h-3.5 w-3.5" />
            Clear
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
        {statusOptions ? (
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Status</Label>
            <select
              value={state.status}
              onChange={(e) => onPatch({ status: e.target.value })}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm"
            >
              <option value="">All</option>
              {statusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {billingStatusOptions ? (
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Billing status</Label>
            <select
              value={state.billing_status}
              onChange={(e) => onPatch({ billing_status: e.target.value })}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm"
            >
              <option value="">All</option>
              {billingStatusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {billingTypeOptions ? (
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Billing type</Label>
            <select
              value={state.billing_type}
              onChange={(e) => onPatch({ billing_type: e.target.value })}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm"
            >
              <option value="">All</option>
              {billingTypeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {fulfilledOptions ? (
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Fulfilled</Label>
            <select
              value={state.fulfilled}
              onChange={(e) => onPatch({ fulfilled: e.target.value })}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm"
            >
              <option value="">All</option>
              {fulfilledOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="space-y-1">
          <Label className="text-xs text-gray-500">User</Label>
          <UserSearchSelect
            value={user.text}
            onChange={user.onChange}
            onSelect={(u) => user.onPick(String(u.uid), `${u.first_name} ${u.last_name}`.trim() || u.email)}
            placeholder="Name or uid"
          />
        </div>

        {showOid ? (
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Org</Label>
            <OrgSearchSelect
              value={org.text}
              onChange={org.onChange}
              onSelect={(o) => org.onPick(String(o.oid), o.name)}
              placeholder="Name or oid"
            />
          </div>
        ) : null}

        {showCcode ? (
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Currency</Label>
            <Input
              value={state.ccode}
              onChange={(e) => onPatch({ ccode: e.target.value.toUpperCase() })}
              placeholder="GHS"
            />
          </div>
        ) : null}

        {showDates ? (
          <>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">From</Label>
              <Input
                type="date"
                value={state.date_from}
                onChange={(e) => onPatch({ date_from: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">To</Label>
              <Input
                type="date"
                value={state.date_to}
                onChange={(e) => onPatch({ date_to: e.target.value })}
              />
            </div>
          </>
        ) : null}

        {showAmount ? (
          <>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Min amount</Label>
              <Input
                type="number"
                step="any"
                value={state.amount_min}
                onChange={(e) => onPatch({ amount_min: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Max amount</Label>
              <Input
                type="number"
                step="any"
                value={state.amount_max}
                onChange={(e) => onPatch({ amount_max: e.target.value })}
              />
            </div>
          </>
        ) : null}

        {extra}
      </div>
    </form>
  );
}

export function SortableTh({
  label,
  field,
  sortBy,
  order,
  onToggle,
}: {
  label: string;
  field: string;
  sortBy: string;
  order: string;
  onToggle: (field: string) => void;
}) {
  const active = sortBy === field;
  return (
    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
      <button
        type="button"
        onClick={() => onToggle(field)}
        className="inline-flex items-center gap-1 hover:text-gray-800"
      >
        {label}
        {active ? <span className="text-[10px]">{order === 'asc' ? '↑' : '↓'}</span> : null}
      </button>
    </th>
  );
}
